/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  selectFlightLegServiceAlerts,
  selectIsPerformanceOutlookLoading,
  selectMissionPlanById,
  selectPerformanceOutlookError,
  selectSelectedOverlaidFlightLegId,
  selectSelectedMissionPlanId,
  selectUserId,
} from '../../../store';
import ErrorMessage from '../../common/layout/ErrorMessage';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import PerformanceOutlookFlightLegList from './PerformanceOutlookFlightLegList';
import PerformanceOutlookHeader from './PerformanceOutlookHeader';
import { Link } from 'react-router-dom';
import PerformanceOutlookHorizontalTimeline, {
  CHART_SIDE_PADDING,
  CHART_WIDTH,
  CHART_TOP_POSITION,
} from './TimelineChart/PerformanceOutlookHorizontalTimeline';
import PerformanceOutlookFlightEventsList from './PerformanceOutlookFlightEventsList';
import { Box } from '@mui/material';
import { PERFORMANCE_OUTLOOK_MAP_ID, setSelectedAntennaTypeId } from '../../../store/maps/actions';
import { switchOverlaysContext } from '../../../store/actions';
import { getFlightLegsInfo } from '../../../store/flightLegs/actions';
import { OverlaysContextsEnum } from '../../../store/maps/reducer';
import { getHnaOverrideData, getMissionPlanById } from '../../../store/missionPlans/actions';
import { getCompactExportEnabled } from '../../../store/config/actions';
import PerformanceOutlookLegInfo from './PerformanceOutlookLegInfo';
import MissionPlanNotLoadedModal from './MissionPlanNotLoadedModal';
import PlansBodyWrapper from '../PlansBodyWrapper';

const LEG_INFO_TOP_POSITION = 560;

interface PerformanceOutlookContainerProps {}

const PerformanceOutlookContainer = (props: PerformanceOutlookContainerProps) => {
  const dispatch = useDispatch();
  const missionPlanId = useSelector(selectSelectedMissionPlanId, shallowEqual);
  const isPOLoading = useSelector(selectIsPerformanceOutlookLoading, shallowEqual);
  const POError = useSelector(selectPerformanceOutlookError, shallowEqual);
  const userId = useSelector(selectUserId, shallowEqual);
  const missionPlan = useSelector(selectMissionPlanById, shallowEqual);
  const serviceAlerts = useSelector(selectFlightLegServiceAlerts, shallowEqual);
  const selectedFlightLegId = useSelector(selectSelectedOverlaidFlightLegId(PERFORMANCE_OUTLOOK_MAP_ID), shallowEqual);

  const [missionPlanLoadError, setMissionPlanLoadError] = useState(false);

  useEffect(() => {
    dispatch(switchOverlaysContext(OverlaysContextsEnum.PERFORMANCE_OUTLOOK));
  }, [dispatch]);

  useEffect(() => {
    if (missionPlanId && userId) {
      dispatch(getCompactExportEnabled(userId));
      dispatch(getMissionPlanById(userId, missionPlanId));
      dispatch(getFlightLegsInfo(missionPlanId, userId));
      dispatch(getHnaOverrideData(missionPlanId, userId));
    }
  }, [dispatch, missionPlanId, userId]);

  useEffect(() => {
    if (missionPlan && missionPlan.antennaTypeId) {
      dispatch(setSelectedAntennaTypeId(PERFORMANCE_OUTLOOK_MAP_ID)(missionPlan.antennaTypeId));
    }
  }, [dispatch, missionPlan]);

  useEffect(() => {
    if (missionPlanId === undefined) {
      setMissionPlanLoadError(true);
    }
  }, [missionPlanId]);

  return (
    <PlansBodyWrapper hideMissionPlanTitle>
      <Box sx={{ position: 'relative', minWidth: `${CHART_WIDTH + 2 * CHART_SIDE_PADDING}px` }}>
        {isPOLoading ? <ModalLoadingSpinner /> : null}
        {POError ? (
          <ErrorMessage>
            <p style={{ margin: '0px' }}>
              An error occurred while updating flight leg. Please go back to the{' '}
              <Link to="/plans" style={{ color: 'inherit', fontSize: 'inherit' }}>
                Mission Plans
              </Link>{' '}
              page.
            </p>
          </ErrorMessage>
        ) : null}
        <PerformanceOutlookHeader />
        <PerformanceOutlookFlightLegList />
        {/* Google Map Placeholder For Reference (components below this will need to be positioned 'absolutely') */}
        <Box
          sx={{
            width: '100%',
            position: 'absolute',
            top: `${LEG_INFO_TOP_POSITION}px`,
          }}
        >
          <PerformanceOutlookLegInfo
            missionPlan={missionPlan}
            serviceAlerts={serviceAlerts}
            flightLegId={selectedFlightLegId}
            serviceTiersSlaChartEnabled={true}
            sx={{
              position: 'relative',
              width: `100%`,
            }}
          />

          <Box
            sx={{
              width: `${CHART_WIDTH}px`,
              margin: 'auto',
              marginTop: '45px',
            }}
          >
            <PerformanceOutlookHorizontalTimeline chartWidth={CHART_WIDTH} chartTopPosition={CHART_TOP_POSITION} />
          </Box>

          <PerformanceOutlookFlightEventsList isPOExportPage={false} />
        </Box>
        <MissionPlanNotLoadedModal
          open={missionPlanLoadError}
          onClose={() => {
            setMissionPlanLoadError(false);
          }}
        />
      </Box>
    </PlansBodyWrapper>
  );
};

export default PerformanceOutlookContainer;
